/**
 * Consecutive-numbering reveal (PR9 wow pass, moment 3).
 *
 * The one purposeful section transition below the fold per
 * .impeccable.md: a two-page mockup illustrating that Bates numbers
 * carry across an entire file set ("file A's last number flows into
 * file B's first"), with no start numbers to track.
 *
 * On scroll into view, the numbers stagger in and the highlighted
 * boundary numbers pulse. Reduced-motion users get the end state
 * instantly (the global rule short-circuits the staggered transitions).
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";

import styles from "./ConsecutiveReveal.module.css";

const FILE_A = ["0043", "0044", "0045"];
const FILE_B = ["0046", "0047", "0048"];

export function ConsecutiveReveal() {
  const sectionRef = useRef<HTMLElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`${styles.section} ${revealed ? styles.revealed : ""}`}
      aria-label="Numbers carry across files"
    >
      <div className={styles.copy}>
        <p className={styles.kicker}>No start numbers to track</p>
        <h2 className={styles.title}>Numbers carry across files.</h2>
        <p className={styles.body}>
          Stamp the first file. Open the next. The first page of B picks up
          exactly where A left off, every time. Imprint counts across the entire
          set.
        </p>
      </div>

      <div className={styles.pages}>
        <Page label="File A" numbers={FILE_A} highlightIndex={FILE_A.length - 1} startDelay={0} />
        <span className={styles.arrow} aria-hidden="true">
          <svg className={styles.arrowIcon} viewBox="0 0 32 16" fill="none">
            <path
              d="M1 8H30M23 2.5 30 8 23 13.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <Page label="File B" numbers={FILE_B} highlightIndex={0} startDelay={FILE_A.length} />
      </div>
    </section>
  );
}

interface PageProps {
  label: string;
  numbers: string[];
  highlightIndex: number;
  startDelay: number;
}

function Page({ label, numbers, highlightIndex, startDelay }: PageProps) {
  return (
    <div className={styles.page}>
      <span className={styles.pageLabel}>{label}</span>
      <div className={styles.pageLines} aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className={styles.stamps}>
        {numbers.map((n, i) => (
          <span
            key={n}
            className={`${styles.stamp} ${i === highlightIndex ? styles.stampHighlight : ""}`}
            style={{ "--i": startDelay + i } as CSSProperties}
          >
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}
